// src/pages/admin/OutilsHeroSliderAdmin.jsx
import React, { useEffect, useState } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";
import { db, storage } from "../firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

export default function OutilsHeroSliderAdmin() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  // Formulaire
  const [form, setForm] = useState({
    type: "image",
    src: "",
    alt: "",
    title: "",
    text: "",
    ctaLabel: "",
    ctaTo: "",
    order: 1,
    isActive: true,
    interval: 3000,
  });

  // State pour upload
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const slidesRef = collection(db, "outils_heroSlides");

  const fetchSlides = async () => {
    setLoading(true);
    try {
      const q = query(slidesRef, orderBy("order", "asc"));
      const snap = await getDocs(q);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSlides(data);
    } catch (e) {
      console.error("Erreur fetchSlides:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlides();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "order" || name === "interval"
          ? Number(value)
          : value,
    }));
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      type: "image",
      src: "",
      alt: "",
      title: "",
      text: "",
      ctaLabel: "",
      ctaTo: "",
      order: 1,
      isActive: true,
      interval: 3000,
    });
    setUploading(false);
    setUploadProgress(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!form.src) {
        alert("L’URL de l’image ou de la vidéo est obligatoire.");
        return;
      }

      const payload = {
        ...form,
        order: form.order || 1,
        interval: form.interval || 3000,
      };

      if (editingId) {
        await updateDoc(doc(db, "outils_heroSlides", editingId), payload);
      } else {
        await addDoc(slidesRef, payload);
      }

      await fetchSlides();
      resetForm();
    } catch (e) {
      console.error("Erreur enregistrement slide:", e);
      alert("Erreur lors de l’enregistrement du slide.");
    }
  };

  const handleEdit = (slide) => {
    setEditingId(slide.id);
    setForm({
      type: slide.type || "image",
      src: slide.src || "",
      alt: slide.alt || "",
      title: slide.title || "",
      text: slide.text || "",
      ctaLabel: slide.ctaLabel || "",
      ctaTo: slide.ctaTo || "",
      order: slide.order || 1,
      isActive: slide.isActive ?? true,
      interval: slide.interval || 3000,
    });
    setUploading(false);
    setUploadProgress(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce slide ?")) return;
    try {
      await deleteDoc(doc(db, "outils_heroSlides", id));
      await fetchSlides();
    } catch (e) {
      console.error("Erreur suppression slide:", e);
    }
  };

  const toggleActive = async (slide) => {
    try {
      await updateDoc(doc(db, "outils_heroSlides", slide.id), {
        isActive: !slide.isActive,
      });
      await fetchSlides();
    } catch (e) {
      console.error("Erreur toggle active:", e);
    }
  };

  // ⬇️ Upload image / GIF vers Firebase Storage
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // On accepte toutes les images, dont .gif
    if (!file.type.startsWith("image/")) {
      alert("Merci de choisir un fichier image (jpg, png, webp, gif…).");
      return;
    }

    const fileName = `${Date.now()}_${file.name}`;
    const storageRef = ref(storage, `outils_heroSlides/${fileName}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    setUploading(true);
    setUploadProgress(0);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const prog =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(Math.round(prog));
      },
      (error) => {
        console.error("Erreur upload:", error);
        alert("Erreur lors de l’upload de l’image.");
        setUploading(false);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          setForm((prev) => ({
            ...prev,
            src: downloadURL,
          }));
          setUploading(false);
          setUploadProgress(100);
        });
      }
    );
  };

  return (
    <div className="container py-4">
      <h2 className="mb-3">Gestion du slider – Outils agricoles</h2>

      {/* FORMULAIRE */}
      <div className="card mb-4">
        <div className="card-header">
          {editingId ? "Modifier un slide" : "Ajouter un nouveau slide"}
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Type</label>
              <select
                className="form-select"
                name="type"
                value={form.type}
                onChange={handleChange}
              >
                <option value="image">Image (y compris GIF)</option>
                <option value="video">Vidéo (URL)</option>
              </select>
            </div>

            {/* Upload image/GIF si type = image */}
            {form.type === "image" && (
              <div className="col-md-9">
                <label className="form-label">
                  Upload image / GIF (remplit automatiquement l’URL)
                </label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*,.gif"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
                {uploading && (
                  <div className="mt-2">
                    <div className="progress" style={{ height: "6px" }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${uploadProgress}%` }}
                        aria-valuenow={uploadProgress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      />
                    </div>
                    <small className="text-muted">
                      Upload en cours… {uploadProgress}%
                    </small>
                  </div>
                )}
                {form.src && !uploading && (
                  <div className="mt-2 d-flex align-items-center gap-2">
                    <img
                      src={form.src}
                      alt="Aperçu"
                      style={{
                        width: 120,
                        height: 60,
                        objectFit: "cover",
                        borderRadius: 4,
                        border: "1px solid #ddd",
                      }}
                    />
                    <small className="text-muted d-block text-truncate">
                      {form.src}
                    </small>
                  </div>
                )}
              </div>
            )}

            {/* Champ URL brut (image ou vidéo) */}
            <div className="col-12">
              <label className="form-label">
                URL {form.type === "image" ? "de l’image" : "de la vidéo"}
              </label>
              <input
                type="text"
                className="form-control"
                name="src"
                value={form.src}
                onChange={handleChange}
                placeholder={
                  form.type === "image"
                    ? "URL d’image (rempli automatiquement après upload, ou collez manuellement)"
                    : "URL vidéo .mp4 ou autre"
                }
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Texte alternatif (alt)</label>
              <input
                type="text"
                className="form-control"
                name="alt"
                value={form.alt}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Titre</label>
              <input
                type="text"
                className="form-control"
                name="title"
                value={form.title}
                onChange={handleChange}
              />
            </div>

            <div className="col-12">
              <label className="form-label">Texte</label>
              <textarea
                className="form-control"
                name="text"
                value={form.text}
                onChange={handleChange}
                rows={2}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Label du bouton (CTA)</label>
              <input
                type="text"
                className="form-control"
                name="ctaLabel"
                value={form.ctaLabel}
                onChange={handleChange}
                placeholder="🔍 Voir le catalogue"
              />
            </div>

            <div className="col-md-8">
              <label className="form-label">Lien du bouton (CTA)</label>
              <input
                type="text"
                className="form-control"
                name="ctaTo"
                value={form.ctaTo}
                onChange={handleChange}
                placeholder="/catalogue, /catalogue-outils?cat=..., /outil/slug..."
              />
            </div>

            <div className="col-md-2">
              <label className="form-label">Ordre</label>
              <input
                type="number"
                className="form-control"
                name="order"
                value={form.order}
                onChange={handleChange}
                min={1}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Intervalle (ms)</label>
              <input
                type="number"
                className="form-control"
                name="interval"
                value={form.interval}
                onChange={handleChange}
                min={1000}
                step={500}
              />
            </div>

            <div className="col-md-3 d-flex align-items-center">
              <div className="form-check mt-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="isActive"
                  id="isActive"
                  checked={form.isActive}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="isActive">
                  Actif
                </label>
              </div>
            </div>

            <div className="col-12 d-flex gap-2 mt-2">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={uploading}
              >
                {editingId ? "Mettre à jour" : "Ajouter"}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={resetForm}
                >
                  Annuler l’édition
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* LISTE DES SLIDES */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>Slides existants</span>
          {loading && <span className="small text-muted">Chargement…</span>}
        </div>
        <div className="card-body p-0">
          {slides.length === 0 ? (
            <p className="p-3 mb-0">Aucun slide pour le moment.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-hover mb-0">
                <thead>
                  <tr>
                    <th>Ordre</th>
                    <th>Aperçu</th>
                    <th>Titre / Texte</th>
                    <th>CTA</th>
                    <th>Actif</th>
                    <th>Intervalle</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {slides.map((s) => (
                    <tr key={s.id}>
                      <td>{s.order}</td>
                      <td style={{ maxWidth: 200 }}>
                        <div className="d-flex align-items-center gap-2">
                          {s.type === "image" ? (
                            <img
                              src={s.src}
                              alt={s.alt}
                              style={{
                                width: 80,
                                height: 45,
                                objectFit: "cover",
                                borderRadius: 4,
                              }}
                            />
                          ) : (
                            <span className="badge bg-dark">Vidéo</span>
                          )}
                          <small className="text-muted d-block text-truncate">
                            {s.alt}
                          </small>
                        </div>
                      </td>
                      <td>
                        <strong>{s.title}</strong>
                        <br />
                        <small className="text-muted">{s.text}</small>
                      </td>
                      <td>
                        {s.ctaLabel && (
                          <>
                            <span className="badge bg-primary">
                              {s.ctaLabel}
                            </span>
                            <br />
                            <small className="text-muted">{s.ctaTo}</small>
                          </>
                        )}
                      </td>
                      <td>
                        <span
                          className={
                            "badge " +
                            (s.isActive ? "bg-success" : "bg-secondary")
                          }
                        >
                          {s.isActive ? "Actif" : "Inactif"}
                        </span>
                      </td>
                      <td>
                        <small>{s.interval || 3000} ms</small>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm" role="group">
                          <button
                            className="btn btn-outline-secondary"
                            onClick={() => handleEdit(s)}
                          >
                            Éditer
                          </button>
                          <button
                            className="btn btn-outline-warning"
                            onClick={() => toggleActive(s)}
                          >
                            {s.isActive ? "Désactiver" : "Activer"}
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(s.id)}
                          >
                            Suppr.
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
